import React from 'react';
import { UpcSupportPage } from './upc-support-page';
import styles from './upc-faqs.module.scss';
import { WAYTO_FAQ_PATH } from '../react-router-paths';

/**
 * Wayto FAQs page
 * Wayto mobile is pointing to Wayto Web's FAQ page. Since we do not have Wayto web anymore,
 * we will have a Wayto FAQs page on Global UPC and have Wayto mobile point to this page
 */
export function WaytoFaqs() {
    return (
        <UpcSupportPage>
            <div className={styles.content}>
                <h1>WayTo&trade; FAQs</h1>
                <h2>General</h2>
                <details>
                    <summary>What is WayTo?</summary>
                    <p>
                        WayTo by Workday is a free mobile app that enables you to easily accept, manage, and share your
                        digital credentials (such as your educational degree and employment history). WayTo is supported
                        on iPhone/iOS/Apple and Android/Google smartphones.
                    </p>
                </details>
                <details>
                    <summary>What is a credential?</summary>
                    <p>
                        Credentials in WayTo express qualifications and achievements (e.g. educational degree,
                        employment history, skills). They are issued by issuers through the Workday Credentials
                        Platform, to individuals who receive them in their WayTo mobile app.
                    </p>
                </details>
                <details>
                    <summary>What is a badge?</summary>
                    <p>
                        A badge is a type of credential that represents an earned skill, certification, or award. Unlike
                        other types of credentials, it can be shared on social media and includes an image.
                    </p>
                </details>
                <details>
                    <summary>What countries and languages is WayTo available in?</summary>
                    <p>
                        At this time, WayTo is available in English only and in the following countries: Australia,
                        Belgium, Canada, Denmark, Finland, France, Germany, Hong Kong, India, Ireland, Italy,
                        Netherlands, New Zealand, Norway, Philippines, Singapore, South Africa, Spain, Sweden,
                        Switzerland, the United Kingdom, and the United States. We are working on adding new countries
                        and languages. If WayTo does not currently appear in your Apple App Store or Google Play, be
                        sure to check back.
                    </p>
                </details>
                <details>
                    <summary>What kind of device and operating system do I need to use WayTo?</summary>
                    <p>
                        For iOS, you must have an iPhone 5S or later model and be running iOS 12 or later. For Android,
                        you must be running Android 8.0 Oreo or later. Android devices vary widely by manufacturer, so
                        your device may not be supported even if it has the updated operating system. If your device
                        does not support WayTo, you will not be able to download the app, or you will get a message
                        saying your device doesn’t support WayTo when you try to open the app.
                    </p>
                </details>
                <h2>Creating and Managing Your Account</h2>
                <details id='how_to_get_wayto_account'>
                    <summary>How can I get a WayTo account?</summary>
                    <p>
                        An issuer, such as your employer, must issue you a credential through the Workday Credentials
                        Platform. You will then receive an email invitation to download WayTo so that you can accept the
                        credential. Install the free WayTo app on your iPhone/iOS/Apple or Android/Google smartphone and
                        activate your account by entering the email address to which your invitation was sent. Follow
                        the remaining instructions to receive a one time passcode to verify your email address and
                        finish set up.
                    </p>
                </details>
                <details>
                    <summary>Why should I create and remember my password or recovery passphrase?</summary>
                    <p>
                        Only you have access to your account backup, but you need a password or recovery passphrase to
                        securely do so. In the event that you need to install WayTo on a new smartphone or re-install on
                        your existing one, you will need the password or recovery passphrase to restore from your
                        account backup. If you hadn't created one or forgot it, your account will need to be deleted, a
                        new one created and credentials re-issued. Please follow specific steps outlined in{' '}
                        <a href={`${WAYTO_FAQ_PATH}#did_not_create_recovery_passphrase`}>
                            "I didn't create a recovery passphrase/password or I forgot it. I am now locked out, what do
                            I do?"
                        </a>
                    </p>
                </details>
                <details>
                    <summary>What is the password or recovery passphrase used for?</summary>
                    <p>
                        A password or recovery passphrase is required to securely access your account backup, in the
                        event that you lose or damage your smartphone or get a new one. It is also needed to securely
                        authenticate into and use the WayTo mobile app, if you choose to not use your device’s
                        authentication features such as Fingerprint, Touch or Face ID with WayTo.
                    </p>
                </details>
                <details>
                    <summary>How do I create or change the password for my WayTo account?</summary>
                    <p>
                        You created a password for WayTo when you set up your WayTo account and backups are
                        automatically updated for you.
                    </p>
                    <p>
                        If you don’t remember the password and/or would like to change it, tap on the "Change Password"
                        option within the menu.
                    </p>
                </details>
                <details>
                    <summary>How do I create or change the recovery passphrase for my WayTo account?</summary>
                    <p>
                        <span className={styles.noteText}>Please Note:</span> If you created your WayTo account after
                        Apr 2020, this question is not applicable to you.
                    </p>
                    <p>
                        You can create a recovery passphrase for WayTo when you set up your WayTo account. You can also
                        tap the "Back Up Now" button on the home screen of the WayTo app or the "Create Recovery
                        Passphrase" option within the menu. Once you have set up a recovery passphrase, backups are
                        automatically updated for you.
                    </p>
                    <p>
                        If you don’t remember the recovery passphrase and/or would like to change it, tap on the "Change
                        Password/Recovery Passphrase" option within the menu.
                    </p>
                </details>
                <details>
                    <summary>Why does WayTo need my mobile phone number?</summary>
                    <p>
                        Coming soon, if you forget your password, we will use your mobile number and email address to
                        confirm your identity so that you can reset your password and recover your account. If you don’t
                        have a mobile number associated with your account, you will not be able to reset your password
                        and recover your account.
                    </p>
                </details>
                <details>
                    <summary>How can I add or change the mobile number associated with my WayTo account?</summary>
                    <p>In the menu, go to Settings&gt;Account&gt;Mobile Phone Number.</p>
                </details>

                <h2>Credentials</h2>
                <details>
                    <summary>What kind of credentials can I get today and how?</summary>
                    <p>
                        Currently, only employers who have accounts on Workday's Credentials Platform can issue you
                        credentials such as proof of employment, certifications, or badges. Ask your employer if they
                        currently use Workday's Credentials Platform. At this time, you cannot issue credentials to
                        yourself.
                    </p>
                </details>
                <details>
                    <summary>What can I do with a credential?</summary>
                    <p>
                        You can share your badges on social media. In the future, you will be able to use your
                        credentials with select online verifiers (e.g. use your proof of employment to verify your
                        eligibility for employee perks and discount programs).
                    </p>
                </details>
                <details>
                    <summary>How else can I share my credentials?</summary>
                    <p>
                        WayTo supports public sharing of badges to social networks and other channels. Select the badge
                        you'd like to share and, at the top right of the badge, you will see the share option.
                    </p>
                </details>
                <details>
                    <summary>What is the QR code scanner for?</summary>
                    <p>
                        If you want to share your credential, (e.g., at a website that supports WayTo as a way to access
                        an employee discount), you will be shown a QR code. When you use WayTo to scan the QR code, you
                        will be prompted to share the credential required.
                    </p>
                </details>
                <details>
                    <summary>How can I delete a credential?</summary>
                    <p>
                        Select the credential you'd like to delete. Then, tap the three dots at the top of the
                        credential to find the delete option. Please note that when you delete a credential, you will
                        lose it permanently. If you want it back, you will need to request that the credential be
                        reissued by the issuer (e.g. an employer).
                    </p>
                </details>
                <details>
                    <summary>I accidentally deleted my credential. How do I get it back?</summary>
                    <p>
                        WayTo does not have access to the details and contents of the credential. If you accidentally
                        deleted a credential, you must contact the issuer and request the credential be issued.
                    </p>
                </details>
                <details>
                    <summary>I didn't receive a credential. What do I do?</summary>
                    <p>
                        WayTo does not have access to the details and contents of the credential. If you haven't
                        received a credential, you must contact the issuer.
                    </p>
                </details>
                <details>
                    <summary>Information in my credential is incorrect. How do I fix it?</summary>
                    <p>
                        Please remember that WayTo does not have access to the content of your credentials. If you have
                        a concern about the information in your credential, you must contact the issuer.
                    </p>
                </details>
                <details>
                    <summary>
                        Where can I find more information about an issuer, including their contact details?
                    </summary>
                    <p>
                        If you have a credential from the issuer in your WayTo account, go to "My Credentials" and tap
                        on that credential. Then tap on the downward facing arrow at the top of the credential to see
                        the issuer's information & contact details.
                    </p>
                </details>
                <details>
                    <summary>What are revoked and expired credentials?</summary>
                    <p>
                        Issuers can withdraw a credential after it has been issued. Such credentials are marked as
                        "Revoked" in the WayTo app. Issuers can also add an expiration date to a credential when issuing
                        it. After the expiration date has passed, the WayTo app will mark that credential as expired.
                        While you can view such credentials within WayTo, you might not be able to use or share them. If
                        you believe that a credential has been revoked or has expired erroneously, please contact the
                        issuer directly.
                    </p>
                </details>
                <h2>Managing Email Addresses</h2>
                <details>
                    <summary>
                        I need to add, delete or make a change to my WayTo account email addresses. What do I do?
                    </summary>
                    <p>
                        At your earliest convenience, authenticate into your WayTo app, go to "Manage Your Emails" in
                        the WayTo menu and choose add, delete, or mark an email primary.
                    </p>
                    <ul>
                        <li>
                            You can add up to three verified email addresses in your WayTo account, as long as these are
                            not associated with another WayTo account. One of these must be the primary email address,
                            which WayTo uses to send you one-time passcodes for account recovery.
                        </li>
                        <li>
                            You can delete a secondary email address at any time, but to delete a primary email address,
                            you must mark one of your other emails primary first.
                        </li>
                        <li>To change an email address, just add a new email and delete the one you no longer need.</li>
                    </ul>
                </details>
                <h2>Recovering Your Account</h2>
                <details>
                    <summary>
                        I previously downloaded the app, created an account, and accepted a credential. But since then I
                        deleted the app, or I no longer have access to that WayTo account. How do I recover my account
                        and the credentials in it?
                    </summary>
                    <p>
                        If you already have a WayTo account with the email address to which a credential was recently
                        issued, but you no longer use the WayTo app or have deleted it, you will need to recover that
                        account to receive your credential. You can do this only if you previously set a recovery
                        passphrase/password. Install the app or re-install, if you have it already, then open it and
                        select the "Recover Existing Account" option. Follow the steps to recover that account and
                        receive your credential.
                    </p>
                </details>
                <details id='did_not_create_recovery_passphrase'>
                    <summary>
                        I didn't create a recovery passphrase/password or I forgot it. I am now locked out, what do I
                        do?
                    </summary>
                    <p>
                        Workday does not have access to your recovery passphrase/password or your account backup. As a
                        result, we are unable to help you recover your password/account.
                    </p>
                </details>
                <details>
                    <summary>
                        I don’t have access to the email address(es) associated with the WayTo account I am trying to
                        recover. What do I do?
                    </summary>
                    <p>
                        Workday does not have access to your WayTo account and cannot update it. If you are unable to
                        access your email address(es) associated with the WayTo account at the time of recovery, you
                        will not be able to recover your WayTo account.
                    </p>
                </details>
                <details id='change_recovery_password'>
                    <summary>How do I change my recovery passphrase/password?</summary>
                    <p>
                        To change your WayTo recovery passphrase/password, you will need to authenticate into your WayTo
                        account on your mobile app. Then go to "Change Password" in the WayTo menu and follow the steps.
                        If you don't have access to your WayTo account and forgot your recovery passphrase/password see{' '}
                        <a href={`${WAYTO_FAQ_PATH}#did_not_create_recovery_passphrase`}>
                            "I didn't create a recovery passphrase/password or I forgot it. What do I do?"
                        </a>
                    </p>
                </details>
                <details>
                    <summary>I lost my smartphone. How do I restore my app?</summary>
                    <p>
                        If you created a recovery passphrase/password, you can install WayTo on a different smartphone
                        and use your recovery passphrase/password to restore your account. If you didn't create a
                        recovery passphrase/password, see{' '}
                        <a href={`${WAYTO_FAQ_PATH}#did_not_create_recovery_passphrase`}>
                            "I didn't create a recovery passphrase/password or I forgot it. What do I do?"
                        </a>
                    </p>
                </details>
                <details>
                    <summary>I bought a new smartphone. How do I restore my app?</summary>
                    <p>
                        On your old smartphone, ensure that you have created a recovery passphrase/password for your
                        WayTo account. Then, install WayTo on your new smartphone and use your recovery
                        passphrase/password to recover your account. For your privacy and security, remember to
                        uninstall WayTo on your old smartphone before you dispose of it.
                    </p>
                </details>
                <details>
                    <summary>I forgot my account password. How do I reset it?</summary>
                    <p>
                        If you are able to authenticate into your WayTo account on your mobile app see{' '}
                        <a href={`${WAYTO_FAQ_PATH}#change_recovery_password`}>
                            "How do I change my recovery passphrase/password?"
                        </a>
                        . If you are locked out, see{' '}
                        <a href={`${WAYTO_FAQ_PATH}#did_not_create_recovery_passphrase`}>
                            "I didn't create a recovery passphrase/password or I forgot it. What do I do?"
                        </a>
                    </p>
                </details>
                <details>
                    <summary>
                        I would like to reinstall WayTo on my smartphone - what should I do before that to ensure I
                        still have my data?
                    </summary>
                    <p>
                        Before you delete the WayTo app, create a recovery passphrase/password for your WayTo account.
                        Then, uninstall and reinstall the app from the Apple App Store or Google Play. Use your recovery
                        passphrase/password to restore your account after reinstalling.
                    </p>
                </details>
                <h2>Deleting Your Account</h2>
                <details>
                    <summary>Can I delete my WayTo account completely?</summary>
                    <p>
                        Yes. You can delete your account and all associated data on your device and on WayTo's
                        platforms. From within the WayTo menu, select "Delete Your Account" and follow the steps.
                    </p>
                </details>
                <h2>Data Privacy</h2>
                <details>
                    <summary>How is my data protected?</summary>
                    <p>
                        Your credentials are encrypted and stored securely. In addition, WayTo uses device-level
                        security, including biometrics such as FaceID and TouchID, to protect your data on your device
                        and on WayTo servers.
                    </p>
                </details>
                <details>
                    <summary>Does Workday have access to my data?</summary>
                    <p>
                        The contents of your credentials are encrypted using your private key, and Workday cannot decode
                        them. However, for support purposes, Workday may access some of your account information. Please
                        view our privacy page for more details.
                    </p>
                </details>
                <details>
                    <summary>Are my credentials public? Who can access them?</summary>
                    <p>
                        Your credentials are private and only you can access them from within the WayTo app. However,
                        when you share a badge publicly on social media or use a credential with a verifier, its details
                        will be accessible by the users on that social media platform or by the verifier.
                    </p>
                </details>
            </div>
        </UpcSupportPage>
    );
}
